import React from 'react';
import styles from './cta-card.module.scss';
import IButton from '../../../models/button/IButton';
import Button from '../../atoms/button/button';
import IImage from '../../../models/viewModels/base/IImage';
import colorStyle from '../../../styles/utils/variables.module.scss';
import { ButtonStyleType } from '../../../constants/button-style-type';
import { ApplicationSettings } from '../../../models/settings';
import ImageComponent from '../../atoms/image-component/image-component';
import Link from 'next/link';

export type CtaCardProps = {
  aspectRatio: string;
  image: IImage;
  title: string;
  copyText: string;
  buttonProps: IButton;
  appSettings: ApplicationSettings;
};

// Sub-component for the image section
const CtaCardImage = ({ image }: { image: IImage }) => {
  const src =
    image?.secureUrl ||
    'https://media.intostudy.com/image/upload/v1681806049/Marketing_Team/Global/Images/PP-news-thumbnail-placeholder.png';
  const altText = image?.alt || 'Image unavailable';
  const width = image?.width ? +image.width : 320;
  const height = image?.height ? +image.height : 200;

  return (
    <div className={styles['cta-card_image']}>
      <ImageComponent src={src} altText={altText} width={width} height={height} />
    </div>
  );
};

// Sub-component for the text and button section with fallback blocks
const CtaCardText = ({
  title,
  copyText,
  buttonProps,
  appSettings,
}: {
  title: string;
  copyText: string;
  buttonProps: IButton;
  appSettings: ApplicationSettings;
}) => {
  const copyColor =
    buttonProps?.buttonStyle === ButtonStyleType.Outline
      ? appSettings.branding.primaryColor
      : colorStyle.white;
  const backgroundColor =
    buttonProps?.buttonStyle === ButtonStyleType.Outline
      ? colorStyle.white
      : appSettings.branding.primaryColor;

  return (
    <div className={styles['cta-card_text']}>
      <h3 className={styles['cta-card_heading']}>
        <Link
          href={buttonProps?.buttonURL || '#'}
          aria-label={`Explore ${title}`}
          target={buttonProps?.buttonNewTab}
        >
          {title || `Title unavailable`}
        </Link>
      </h3>
      <p className={styles['cta-card_desc']}>{copyText || `Description unavailable`}</p>
      <div className={styles['cta-card_button']}>
        {buttonProps ? (
          <Button
            buttonID={buttonProps.buttonID}
            buttonURL={buttonProps.buttonURL}
            buttonNewTab={buttonProps.buttonNewTab}
            buttonAriaLabel={buttonProps.buttonAriaLabel}
            buttonRole={buttonProps.buttonRole}
            buttonName={buttonProps.buttonName}
            buttonSize={buttonProps.buttonSize}
            buttonStyle={buttonProps.buttonStyle}
            copyColor={copyColor}
            backgroundColor={backgroundColor}
            outlineColor={appSettings.branding.primaryColor}
            hoverBackgroundColor={appSettings.branding.primaryHoverColor}
            activeBackgroundColor={appSettings.branding.primaryActiveColor}
            heapIdentifier={buttonProps.buttonID}
            buttonType={buttonProps.buttonType}
          >
            {buttonProps.children}
          </Button>
        ) : (
          <Button
            buttonName='Link unavailable'
            buttonURL='#'
            buttonStyle={ButtonStyleType.Filled}
            copyColor={copyColor}
            backgroundColor={backgroundColor}
            outlineColor={appSettings.branding.primaryColor}
            hoverBackgroundColor={appSettings.branding.primaryHoverColor}
            activeBackgroundColor={appSettings.branding.primaryActiveColor}
          >
            Link unavailable
          </Button>
        )}
      </div>
    </div>
  );
};

// Main CtaCard component
export default function CtaCard({
  aspectRatio,
  image,
  title,
  copyText,
  buttonProps,
  appSettings,
}: CtaCardProps) {
  return (
    <div
      className={`${styles['cta-card']} ${
        aspectRatio === 'keep ratio' ? styles['keep-ratio'] : styles['fill-height']
      }`}
    >
      <div className={styles['cta-card_content']}>
        <CtaCardImage image={image} />
        <CtaCardText
          title={title}
          copyText={copyText}
          buttonProps={buttonProps}
          appSettings={appSettings}
        />
      </div>
    </div>
  );
}


